/**
 * Pure HTML builders for módulo Leads (Sales Analytics / embudo).
 *
 * @module-isolated analytics-leads-html
 * @depends analytics-config.js (solo lectura)
 * @consumers modules/leads/LeadsModule.jsx
 */
import { ANALYTICS_CONFIG } from '../../../config/analytics-config.js';

export function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function dash(v) {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

export function fmtNum(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('es-CL');
}

export function fmtMinutes(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  if (n < 60) return n + ' min';
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return h + ' h ' + m + ' min';
}

export function pct(part, total) {
  const p = Number(part);
  const t = Number(total);
  if (!t || Number.isNaN(p) || Number.isNaN(t)) return null;
  return Math.round((p / t) * 1000) / 10;
}

export function fmtPct(part, total) {
  const p = pct(part, total);
  return p == null ? '—' : String(p).replace('.', ',') + '%';
}

/** Fechas legibles (sin ISO crudo). */
export function formatHumanDate(isoOrDate) {
  if (!isoOrDate) return null;
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatShortDate(isoOrDate) {
  if (!isoOrDate) return null;
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatRelativeAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'hace instantes';
  if (mins < 60) return 'hace ' + mins + ' min';
  const hours = Math.round(mins / 60);
  if (hours < 48) return 'hace ' + hours + ' h';
  const days = Math.round(hours / 24);
  return 'hace ' + days + ' d';
}

export function kommoLeadUrl(leadId) {
  const tpl = ANALYTICS_CONFIG.kommoLeadUrlTemplate;
  if (!tpl || !leadId) return null;
  return String(tpl).replace('{id}', encodeURIComponent(String(leadId)));
}

export function sellerLabel(sellerId, displayName) {
  const id = String(sellerId || '').trim();
  if (!id || id === 'sin_asignar') {
    return { title: 'Sin asignar', sub: null };
  }
  const dn = String(displayName || '');
  if (dn && !/^Kommo\s*#/i.test(dn) && dn !== id) {
    return { title: dn, sub: 'ID ' + id };
  }
  return { title: 'Vendedor no identificado', sub: 'ID ' + id };
}

function warningLevel(text) {
  const w = String(text || '').toLowerCase();
  if (/desactualiz|sin registro|sync kommo|fuera de sla|requieren atención|error|falló|fail/.test(w)) {
    return 'critical';
  }
  if (/cobertura|seguimiento|sin creado|advertencia|warning|15\+|backlog|muestra/.test(w)) {
    return 'attention';
  }
  return 'info';
}

function reasonLabel(r) {
  const map = {
    sin_procesar_demorado: 'Sin procesar > plazo',
    en_proceso_estancado: 'En proceso estancado',
    sin_contactar: 'Sin contactar',
    fuera_sla_primer_contacto: 'Fuera SLA',
    sla_pendiente: 'SLA pendiente',
    antiguedad_alta: 'Antigüedad alta'
  };
  return map[r] || r;
}

export function sortSellers(list, mode) {
  const arr = (list || []).slice();
  const crit = (s) => {
    const open = Number(s.openLeads) || 0;
    const sla = Number(s.slaBreached) || 0;
    return open ? sla / open : 0;
  };
  if (mode === 'sla') arr.sort((a, b) => (Number(b.slaBreached) || 0) - (Number(a.slaBreached) || 0));
  else if (mode === 'open') arr.sort((a, b) => (Number(b.openLeads) || 0) - (Number(a.openLeads) || 0));
  else if (mode === 'uncontacted') {
    arr.sort((a, b) => (Number(b.uncontacted) || 0) - (Number(a.uncontacted) || 0));
  } else arr.sort((a, b) => crit(b) - crit(a) || (Number(b.slaBreached) || 0) - (Number(a.slaBreached) || 0));
  return arr;
}

/**
 * Meta bar content (same markup as original renderMetaBar innerHTML).
 * @returns {string}
 */
export function buildMetaBarText(coverage, kommoSync) {
  const parts = [];
  if (coverage && coverage.exactFrom) {
    const human = formatShortDate(coverage.exactFrom) || coverage.exactFrom;
    parts.push('Cobertura exacta desde <strong>' + esc(human) + '</strong>');
  } else {
    parts.push('Cobertura de etapas: historial aún no inicializado');
  }
  if (kommoSync && kommoSync.lastSyncAt) {
    const rel = formatRelativeAgo(kommoSync.lastSyncAt) || formatHumanDate(kommoSync.lastSyncAt);
    const stale = kommoSync.stale === true;
    parts.push(
      'Última sincronización: <span class="' +
        (stale ? 'is-stale' : '') +
        '">' +
        esc(rel) +
        (stale ? ' (desactualizado)' : '') +
        '</span>'
    );
  } else {
    parts.push('<span class="is-stale">Última sincronización: sin registro</span>');
  }
  return parts.join(' · ');
}

export function buildCriticalHtml(attention, agingBuckets, kpis) {
  const totalAtt = attention && attention.total != null ? Number(attention.total) : 0;
  const aging = Array.isArray(agingBuckets) ? agingBuckets : [];
  const plus15 = aging.find((b) => b.key === '15_plus');
  const plus15n = plus15 ? Number(plus15.count) || 0 : 0;
  const sla = kpis && kpis.slaBreachedFirstContact != null ? Number(kpis.slaBreachedFirstContact) : 0;
  const open = kpis && kpis.openLeads != null ? Number(kpis.openLeads) : 0;

  if (!totalAtt && !sla && !plus15n) {
    return (
      '<div class="analytics-critical-card is-ok">' +
      '<p class="analytics-critical-value">Sin backlog crítico</p>' +
      '<p class="analytics-critical-sub">No hay leads en cola de atención según las reglas actuales.</p>' +
      '</div>'
    );
  }

  const bits = [];
  if (plus15n) {
    bits.push(
      fmtNum(plus15n) +
        ' llevan más de 15 días abiertos' +
        (open ? ' (' + fmtPct(plus15n, open) + ' del stock)' : '')
    );
  }
  if (sla) bits.push(fmtNum(sla) + ' están fuera del SLA de primer contacto');
  const pending = kpis && kpis.slaPendingFirstContact != null ? Number(kpis.slaPendingFirstContact) : 0;
  if (pending) bits.push(fmtNum(pending) + ' aún dentro del plazo SLA (pendientes)');

  return (
    '<div class="analytics-critical-card is-urgent">' +
    '<p class="analytics-critical-value">' +
    fmtNum(totalAtt) +
    ' leads requieren atención</p>' +
    '<p class="analytics-critical-sub">' +
    esc(bits.length ? bits.join(' · ') + '.' : 'Revisa embudo, aging y cola crítica.') +
    '</p>' +
    '</div>'
  );
}

export function buildKpisHtml(kpis, dataQuality, loading) {
  const medianAge =
    dataQuality && dataQuality.openAgeDays && dataQuality.openAgeDays.median != null
      ? dataQuality.openAgeDays.median
      : null;
  const sample = kpis && kpis.firstContactSampleSize != null ? Number(kpis.firstContactSampleSize) : 0;
  const contactTxt =
    !loading && sample <= 0
      ? 'Sin datos suficientes'
      : loading
        ? '…'
        : fmtMinutes(kpis && kpis.medianFirstContactMinutes) || 'Sin datos suficientes';
  const pending = kpis && kpis.slaPendingFirstContact != null ? kpis.slaPendingFirstContact : null;
  const slaVal = loading ? '…' : fmtNum(kpis && kpis.slaBreachedFirstContact);
  const slaHint =
    pending != null
      ? 'Pendientes dentro de plazo: ' + fmtNum(pending)
      : 'Contactados tarde + sin contactar con plazo vencido';

  const cards = [
    {
      title: 'Leads abiertos',
      value: loading ? '…' : fmtNum(kpis && kpis.openLeads),
      hint: 'Stock actual en embudo',
      tip: 'Leads efectivamente abiertos según mapa de etapas y closedAt.',
      state: 'ok'
    },
    {
      title: 'Fuera de SLA',
      value: slaVal,
      hint: slaHint,
      tip: 'Fuera del SLA laboral de primer contacto (contactados tarde o sin contactar con plazo vencido).',
      state: !loading && kpis && Number(kpis.slaBreachedFirstContact) > 0 ? 'bad' : 'ok'
    },
    {
      title: 'Antigüedad mediana',
      value: loading ? '…' : medianAge != null ? fmtNum(medianAge) + ' d' : 'Sin datos suficientes',
      hint: 'Días desde creación (abiertos)',
      tip: 'Mediana de días abiertos desde Creado at, solo leads abiertos.',
      state: !loading && medianAge != null && medianAge >= 15 ? 'warn' : 'muted'
    },
    {
      title: 'Ingresados en el período',
      value: loading ? '…' : fmtNum(kpis && kpis.createdInPeriod),
      hint: 'Según Creado at en el filtro',
      tip: 'Cohorte: leads creados en el período seleccionado.',
      state: 'info'
    },
    {
      title: '1er contacto (mediana)',
      value: contactTxt,
      hint: sample > 0 ? 'Muestra: ' + fmtNum(sample) : 'Cobertura de seguimiento insuficiente',
      tip: 'Mediana de minutos laborales al primer contacto. Requiere Seguimiento_Leads.',
      state: sample > 0 ? 'ok' : 'muted'
    }
  ];

  return cards
    .map((c) => {
      const st =
        c.state === 'bad'
          ? ' is-bad'
          : c.state === 'warn'
            ? ' is-warn'
            : c.state === 'ok'
              ? ' is-ok'
              : ' is-muted';
      return (
        '<article class="analytics-kpi-card' +
        st +
        '">' +
        '<p class="analytics-kpi-label" title="' +
        esc(c.tip) +
        '">' +
        esc(c.title) +
        '</p>' +
        '<p class="analytics-kpi-value">' +
        esc(c.value) +
        '</p>' +
        '<p class="analytics-kpi-hint">' +
        esc(c.hint) +
        '</p></article>'
      );
    })
    .join('');
}

export function buildFunnelHtml(funnel, fallbackStages) {
  const openList =
    (funnel && Array.isArray(funnel.open) && funnel.open) ||
    (Array.isArray(fallbackStages) ? fallbackStages.map((s) => ({ ...s, sla: null })) : []);
  const closedList = (funnel && Array.isArray(funnel.closed) && funnel.closed) || [];
  const totals = (funnel && funnel.totals) || {
    open: openList.reduce((a, s) => a + (Number(s.count) || 0), 0),
    closed: closedList.reduce((a, s) => a + (Number(s.count) || 0), 0)
  };
  const legend = (funnel && funnel.slaLegend) || {
    ok: '0–1 día (o dentro de SLA)',
    warn: '2–7 días · o reunión ya pasó (≤1 día de gracia)',
    bad: '8+ días / fuera SLA 1er contacto · o >1 día post-reunión aún en «agendada»',
    hold: 'Reunión/visita agendada a futuro (o sin fecha en calendario)',
    unknown: 'Sin fecha de creación'
  };

  if (!openList.length && !closedList.length) {
    return '<p class="analytics-empty">Sin datos de embudo.</p>';
  }

  const openTotal = Number(totals.open) || openList.reduce((a, s) => a + (Number(s.count) || 0), 0) || 1;
  const maxOpen = Math.max.apply(
    null,
    openList.map((r) => Number(r.count) || 0).concat([1])
  );

  function stageLabel(r) {
    const stage = r.stageName || r.stageId || '—';
    const pipe = r.pipelineName ? ' · ' + r.pipelineName : '';
    const state = r.internalStateName ? ' → ' + r.internalStateName : '';
    return stage + pipe + state;
  }

  function slaStack(sla, total) {
    if (!sla || !total) return '';
    const parts = [
      ['ok', sla.ok],
      ['warn', sla.warn],
      ['bad', sla.bad],
      ['hold', sla.hold],
      ['unknown', sla.unknown]
    ];
    return (
      '<div class="analytics-sla-stack" aria-hidden="true">' +
      parts
        .map(([key, n]) => {
          const c = Number(n) || 0;
          if (!c) return '';
          return (
            '<span class="analytics-sla-seg is-' +
            key +
            '" style="flex:' +
            c +
            '" title="' +
            esc(legend[key] || key) +
            ': ' +
            c +
            '"></span>'
          );
        })
        .join('') +
      '</div>'
    );
  }

  const openHtml = !openList.length
    ? '<p class="analytics-empty">Sin leads abiertos.</p>'
    :       '<div class="analytics-sla-legend">' +
      '<span><i class="analytics-sla-swatch is-ok"></i>' +
      esc(legend.ok) +
      '</span>' +
      '<span><i class="analytics-sla-swatch is-warn"></i>' +
      esc(legend.warn) +
      '</span>' +
      '<span><i class="analytics-sla-swatch is-bad"></i>' +
      esc(legend.bad) +
      '</span>' +
      (legend.hold
        ? '<span><i class="analytics-sla-swatch is-hold"></i>' + esc(legend.hold) + '</span>'
        : '') +
      '</div>' +
      '<div class="analytics-funnel-list">' +
      openList
        .map((r) => {
          const c = Number(r.count) || 0;
          const barPct = Math.round((c / maxOpen) * 100);
          const share = fmtPct(c, openTotal);
          const badN = r.sla && r.sla.bad != null ? Number(r.sla.bad) || 0 : 0;
          const holdN = r.sla && r.sla.hold != null ? Number(r.sla.hold) || 0 : 0;
          const warnN = r.sla && r.sla.warn != null ? Number(r.sla.warn) || 0 : 0;
          const stageName = String(r.stageName || '').toLowerCase();
          const isHoldStage =
            /reuni[oó]n\s*agendada/.test(stageName) || /visita\s*agendada/.test(stageName);
          let foot = '';
          if (isHoldStage) {
            const bits = [];
            if (holdN) {
              bits.push(
                '<span class="is-hold">' +
                  fmtNum(holdN) +
                  ' con reunión a futuro / sin fecha en calendario</span>'
              );
            }
            if (warnN) {
              bits.push(
                '<span class="is-warn">' +
                  fmtNum(warnN) +
                  ' reunión ya pasó (≤1 día de gracia)</span>'
              );
            }
            if (badN) {
              bits.push(
                '<span class="is-bad">' +
                  fmtNum(badN) +
                  ' >1 día post-reunión sin cambiar de etapa</span>'
              );
            }
            if (bits.length) {
              foot =
                '<div class="analytics-funnel-foot">' + bits.join(' · ') + '</div>';
            }
          } else if (badN) {
            foot =
              '<div class="analytics-funnel-foot"><span class="is-bad">' +
              fmtNum(badN) +
              ' en rango crítico (8+ / fuera SLA)</span></div>';
          }
          return (
            '<div class="analytics-funnel-row">' +
            '<div class="analytics-funnel-top">' +
            '<div class="analytics-funnel-name">' +
            esc(stageLabel(r)) +
            '</div>' +
            '<div class="analytics-funnel-stats"><strong>' +
            fmtNum(c) +
            '</strong> leads · ' +
            esc(share) +
            '</div></div>' +
            '<div class="analytics-bar-track"><div class="analytics-bar-fill" style="width:' +
            barPct +
            '%"></div></div>' +
            slaStack(r.sla, c) +
            foot +
            '</div>'
          );
        })
        .join('') +
      '</div>';

  const closedHtml = !closedList.length
    ? '<p class="analytics-empty">Sin leads en etapas cerradas.</p>'
    : '<div class="analytics-closed-list">' +
      closedList
        .map((r) => {
          let chip = 'cerrada';
          let chipClass = 'is-info';
          if (r.isSuccess || r.resultado === 'ganado') {
            chip = 'ganado';
            chipClass = 'is-ok';
          } else if (r.isFailure || r.resultado === 'perdido') {
            chip = 'perdido';
            chipClass = 'is-bad';
          } else if (r.resultado === 'no_aplica') {
            chip = 'no aplica';
            chipClass = 'is-warn';
          } else if (r.internalStateName) {
            chip = String(r.internalStateName).toLowerCase();
          }
          return (
            '<div class="analytics-closed-row">' +
            '<span><span class="analytics-badge ' +
            chipClass +
            '">' +
            esc(chip) +
            '</span> ' +
            esc(stageLabel(r)) +
            '</span><strong>' +
            fmtNum(r.count) +
            '</strong></div>'
          );
        })
        .join('') +
      '</div>';

  return (
    '<div class="analytics-funnel-block">' +
    '<p class="analytics-section-note" style="margin-top:0">Abiertas · ' +
    fmtNum(totals.open) +
    '</p>' +
    openHtml +
    '</div>' +
    '<details class="analytics-details" style="margin-top:16px;border:0;padding:0;background:transparent">' +
    '<summary>Etapas finales · ' +
    fmtNum(totals.closed) +
    '</summary>' +
    closedHtml +
    '</details>'
  );
}

export function buildAgingHtml(buckets, dataQuality) {
  const list = Array.isArray(buckets) ? buckets : [];
  if (!list.length) {
    return '<p class="analytics-empty">Sin datos.</p>';
  }
  const known = list.filter((r) => r.key !== 'unknown');
  const totalKnown = known.reduce((a, r) => a + (Number(r.count) || 0), 0);
  const plus = list.find((r) => r.key === '15_plus');
  const plusN = plus ? Number(plus.count) || 0 : 0;
  const plusPct = pct(plusN, totalKnown);
  const max = Math.max.apply(
    null,
    list.map((r) => Number(r.count) || 0).concat([1])
  );
  const age = (dataQuality && dataQuality.openAgeDays) || {};
  const range = (dataQuality && dataQuality.openCreatedAtRange) || {};

  let exec = '';
  if (plusPct != null && plusN > 0) {
    exec =
      '<p class="analytics-exec-line"><strong>' +
      esc(String(plusPct).replace('.', ',')) +
      '%</strong> de los leads abiertos tiene más de 15 días.</p>';
  }

  const meta =
    '<div class="analytics-aging-meta">' +
    '<span>Mediana: <strong>' +
    (age.median != null ? fmtNum(age.median) + ' d' : '—') +
    '</strong></span>' +
    '<span>Rango: <strong>' +
    (age.min != null ? fmtNum(age.min) : '—') +
    '–' +
    (age.max != null ? fmtNum(age.max) : '—') +
    ' d</strong></span>' +
    '<span>Más reciente: <strong>' +
    esc(formatShortDate(range.newest) || '—') +
    '</strong></span>' +
    '</div>';

  return (
    exec +
    meta +
    '<div class="analytics-bars">' +
    list
      .map((r) => {
        const c = Number(r.count) || 0;
        const barPct = Math.round((c / max) * 100);
        const dominant = r.key === '15_plus' && plusPct != null && plusPct >= 50;
        return (
          '<div class="analytics-bar-row' +
          (dominant ? ' is-dominant' : '') +
          '"><div>' +
          esc(r.label || r.key) +
          '</div><div class="analytics-bar-track"><div class="analytics-bar-fill' +
          (r.key === '15_plus' || r.key === '8_14' ? ' is-warn' : '') +
          '" style="width:' +
          barPct +
          '%"></div></div><div class="analytics-num">' +
          fmtNum(c) +
          '</div></div>'
        );
      })
      .join('') +
    '</div>'
  );
}

export function cicloLabel(ciclo) {
  if (ciclo === 'terminado') return 'Terminado';
  if (ciclo === 'en_proceso') return 'En proceso';
  return 'Sin procesar';
}

export function cicloBadge(ciclo) {
  const id =
    ciclo === 'terminado' ? 'terminado' : ciclo === 'en_proceso' ? 'en_proceso' : 'sin_procesar';
  return (
    '<span class="analytics-ciclo-badge is-' + id + '">' + esc(cicloLabel(id)) + '</span>'
  );
}

/**
 * Radiografía comercial: los 3 ciclos con sus subestados, cantidad y antigüedad.
 * Cada estado interno se puede expandir para ver Embudo → Etapa Kommo.
 * Sin procesar / En proceso = stock abierto en vivo (no depende de fechas).
 * Terminado = cierres del período (Ganado / Perdido / No aplica).
 */
export function buildCommercialHtml(snapshot, kpis) {
  if (!snapshot) return '<p class="analytics-empty">Sin datos.</p>';
  const k = kpis || {};
  const states = Array.isArray(snapshot.byState) ? snapshot.byState : [];

  // Nested: Embudo → Etapa · Leads · % · (Antig. mediana solo en ciclos abiertos)
  const kommoStageRows = (stages, parentCount, withAge) => {
    const list = Array.isArray(stages) ? stages : [];
    if (!list.length) {
      return (
        '<tr class="analytics-kommo-empty"><td colspan="' +
        (withAge ? 4 : 3) +
        '"><span class="analytics-sub">Sin etapas Kommo mapeadas.</span></td></tr>'
      );
    }
    return list
      .map((st) => {
        const pct =
          st.share != null
            ? st.share
            : parentCount
              ? Math.round((st.count / parentCount) * 1000) / 10
              : 0;
        return (
          '<tr class="analytics-kommo-row">' +
          '<td><span class="analytics-kommo-pipe">' +
          esc(st.pipelineName || st.pipelineId || '—') +
          '</span><span class="analytics-sub">' +
          esc(st.stageName || st.stageId || '—') +
          '</span></td>' +
          '<td class="num">' +
          fmtNum(st.count) +
          '</td>' +
          '<td class="num">' +
          fmtNum(pct) +
          '%</td>' +
          (withAge
            ? '<td class="num">' +
              (st.medianAgeDays != null ? fmtNum(st.medianAgeDays) + ' d' : '—') +
              '</td>'
            : '') +
          '</tr>'
        );
      })
      .join('');
  };

  // Nivel 1 (abierto): Estado · Leads · Antig. mediana · Críticos (menos columnas).
  const openCard = (opts) => {
    const list = states.filter((s) => s.ciclo === opts.ciclo);
    const total = Number(opts.total) || 0;
    const rows = !list.length
      ? '<tr><td colspan="4" class="analytics-empty" style="padding:8px">Sin leads en este ciclo.</td></tr>'
      : list
          .map((s) => {
            const stages = Array.isArray(s.byKommoStage) ? s.byKommoStage : [];
            const canExpand = stages.length > 0;
            const critTxt =
              s.critDays != null ? 'crítico > ' + fmtNum(s.critDays) + ' d' : '';
            const name = esc(s.internalStateName || s.internalStateId || '—');
            const labelCell = canExpand
              ? '<details class="analytics-expand">' +
                '<summary><span class="analytics-expand-name"><strong>' +
                name +
                '</strong>' +
                (critTxt ? '<span class="analytics-sub">' + critTxt + '</span>' : '') +
                '</span><span class="analytics-expand-hint">Ver ' +
                fmtNum(stages.length) +
                ' etapa(s) Kommo</span></summary></details>'
              : '<strong>' +
                name +
                '</strong>' +
                (critTxt ? '<span class="analytics-sub">' + critTxt + '</span>' : '');
            const main =
              '<tr class="analytics-state-row">' +
              '<td>' +
              labelCell +
              '</td>' +
              '<td class="num">' +
              fmtNum(s.count) +
              '</td>' +
              '<td class="num">' +
              (s.medianAgeDays != null ? fmtNum(s.medianAgeDays) + ' d' : '—') +
              '</td>' +
              '<td class="num' +
              ((s.crit || 0) > 0 ? ' is-bad' : '') +
              '" title="' +
              (s.critDays != null ? '> ' + s.critDays + ' d' : '') +
              '">' +
              fmtNum(s.crit) +
              '</td>' +
              '</tr>';
            if (!canExpand) return main;
            return (
              main +
              '<tr class="analytics-kommo-wrap">' +
              '<td colspan="4">' +
              '<div class="analytics-kommo-panel">' +
              '<table class="analytics-table analytics-kommo-table"><thead><tr>' +
              '<th>Embudo → Etapa (Kommo)</th>' +
              '<th class="num">Leads</th>' +
              '<th class="num">%</th>' +
              '<th class="num">Antig. mediana</th>' +
              '</tr></thead><tbody>' +
              kommoStageRows(stages, s.count, true) +
              '</tbody></table></div></td></tr>'
            );
          })
          .join('');
    return (
      '<article class="analytics-panel analytics-cycle-card">' +
      '<header class="analytics-cycle-head">' +
      '<span class="analytics-cycle-total">' +
      fmtNum(total) +
      '</span>' +
      '<span class="analytics-cycle-titlewrap">' +
      '<span class="analytics-ciclo-badge is-' +
      opts.ciclo +
      '">' +
      esc(opts.label) +
      '</span>' +
      '<span class="analytics-chip is-live">Foto en vivo</span>' +
      '</span>' +
      '</header>' +
      '<p class="analytics-sub" style="margin:2px 0 8px">' +
      esc(opts.note) +
      ' · Antig. mediana ' +
      (opts.medianAge != null ? fmtNum(opts.medianAge) + ' d' : '—') +
      ' · Tocá un estado para ver Embudo → Etapa</p>' +
      '<table class="analytics-table analytics-commercial-table"><thead><tr>' +
      '<th>Estado interno</th>' +
      '<th class="num">Leads</th>' +
      '<th class="num">Antig. mediana</th>' +
      '<th class="num">Críticos</th>' +
      '</tr></thead><tbody>' +
      rows +
      '</tbody></table>' +
      '</article>'
    );
  };

  const spCard = openCard({
    ciclo: 'sin_procesar',
    label: 'Sin procesar',
    total: snapshot.sinProcesar,
    medianAge: snapshot.medianAgeSinProcesar,
    note: 'Ingresó pero aún sin gestión'
  });
  const epCard = openCard({
    ciclo: 'en_proceso',
    label: 'En proceso',
    total: snapshot.enProceso,
    medianAge: snapshot.medianAgeEnProceso,
    note: 'Ya en gestión (contactado, reunión, abono…)'
  });

  // Terminado: cierres del período, expandibles a Embudo → Etapa.
  const ter = snapshot.terminated || {};
  const terList = Array.isArray(ter.byResultado) ? ter.byResultado : [];
  const terTotal = ter.total != null ? Number(ter.total) : Number(k.closedInPeriod) || 0;
  const terRows = !terList.length
    ? [
        { label: 'Ganado', count: k.soldInPeriod, byKommoStage: [] },
        { label: 'Perdido', count: k.notSoldInPeriod, byKommoStage: [] },
        { label: 'No aplica', count: k.notApplicableInPeriod, byKommoStage: [] }
      ]
        .concat(
          Number(k.otherFinalInPeriod) > 0
            ? [{ label: 'Otros', count: k.otherFinalInPeriod, byKommoStage: [] }]
            : []
        )
        .map((r) => {
          const pct = terTotal ? Math.round(((Number(r.count) || 0) / terTotal) * 1000) / 10 : 0;
          return (
            '<tr><td><strong>' +
            esc(r.label) +
            '</strong></td><td class="num">' +
            fmtNum(r.count) +
            '</td><td class="num">' +
            fmtNum(pct) +
            '%</td></tr>'
          );
        })
        .join('')
    : terList
        .map((r) => {
          const pct = terTotal ? Math.round(((Number(r.count) || 0) / terTotal) * 1000) / 10 : 0;
          const stages = Array.isArray(r.byKommoStage) ? r.byKommoStage : [];
          const canExpand = stages.length > 0;
          const labelCell = canExpand
            ? '<details class="analytics-expand">' +
              '<summary><span class="analytics-expand-name"><strong>' +
              esc(r.label) +
              '</strong></span><span class="analytics-expand-hint">Ver ' +
              fmtNum(stages.length) +
              ' etapa(s) Kommo</span></summary></details>'
            : '<strong>' + esc(r.label) + '</strong>';
          const main =
            '<tr class="analytics-state-row">' +
            '<td>' +
            labelCell +
            '</td>' +
            '<td class="num">' +
            fmtNum(r.count) +
            '</td>' +
            '<td class="num">' +
            fmtNum(pct) +
            '%</td></tr>';
          if (!canExpand) return main;
          return (
            main +
            '<tr class="analytics-kommo-wrap">' +
            '<td colspan="3">' +
            '<div class="analytics-kommo-panel">' +
            '<table class="analytics-table analytics-kommo-table"><thead><tr>' +
            '<th>Embudo → Etapa (Kommo)</th>' +
            '<th class="num">Leads</th>' +
            '<th class="num">%</th>' +
            '</tr></thead><tbody>' +
            kommoStageRows(stages, r.count, false) +
            '</tbody></table></div></td></tr>'
          );
        })
        .join('');

  const rate = k.salesDecisionRate == null ? null : Number(k.salesDecisionRate);
  const terCard =
    '<article class="analytics-panel analytics-cycle-card">' +
    '<header class="analytics-cycle-head">' +
    '<span class="analytics-cycle-total">' +
    fmtNum(terTotal) +
    '</span>' +
    '<span class="analytics-cycle-titlewrap">' +
    '<span class="analytics-ciclo-badge is-terminado">Terminado</span>' +
    '<span class="analytics-chip is-period">Período</span>' +
    '</span>' +
    '</header>' +
    '<p class="analytics-sub" style="margin:2px 0 8px">Cierres del período (según filtro de fechas) · Tocá un resultado para ver Embudo → Etapa</p>' +
    '<table class="analytics-table analytics-commercial-table"><thead><tr>' +
    '<th>Resultado</th><th class="num">Leads</th><th class="num">%</th>' +
    '</tr></thead><tbody>' +
    terRows +
    '</tbody></table>' +
    '<p class="analytics-section-note" style="margin-top:8px">Tasa de cierre ' +
    (rate == null ? '—' : fmtNum(rate) + '%') +
    ' (sobre Ganado + Perdido) · Ingresados en período ' +
    fmtNum(k.createdInPeriod) +
    '.</p>' +
    '</article>';

  const gen = formatHumanDate(snapshot.generatedAt);
  const foot =
    '<p class="analytics-section-note" style="margin-top:8px">Sin procesar y En proceso: foto en vivo del stock abierto, no depende del filtro de fechas' +
    (gen ? ' · ' + esc(gen) : '') +
    (Number(snapshot.noCreatedAt) > 0
      ? ' · ' + fmtNum(snapshot.noCreatedAt) + ' sin fecha de creación'
      : '') +
    '.</p>';

  return '<div class="analytics-commercial-grid">' + spCard + epCard + terCard + '</div>' + foot;
}

export function fmtMoneyClp(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return (
    '$' +
    Math.round(Number(v)).toLocaleString('es-CL', { maximumFractionDigits: 0 })
  );
}

/**
 * Cruce gasto Meta → camino Kommo/reuniones/propuesta/reserva por campaña.
 * El período es el DateRangeFilter de Leads (dateFrom/dateTo).
 */
export function buildGastoRetornoHtml(gastoRetorno) {
  if (!gastoRetorno) {
    return '<p class="analytics-empty">Sin cruce gasto→retorno para el período.</p>';
  }
  const t = gastoRetorno.totals || {};
  const camps = Array.isArray(gastoRetorno.campaigns) ? gastoRetorno.campaigns : [];
  const diag = gastoRetorno.diagnostics || {};
  const camino = Array.isArray(gastoRetorno.camino) ? gastoRetorno.camino : [];
  const windowDays = gastoRetorno.windowDays != null ? gastoRetorno.windowDays : 21;
  const range =
    (gastoRetorno.dateFrom || '—') + ' → ' + (gastoRetorno.dateTo || '—');

  function fmtCaminoValue(step) {
    if (!step || step.value == null || Number.isNaN(Number(step.value))) return '—';
    if (step.unit === 'clp') return fmtMoneyClp(step.value);
    if (step.unit === 'uf') {
      return (
        Number(step.value).toLocaleString('es-CL', { maximumFractionDigits: 1 }) + ' UF'
      );
    }
    return fmtNum(step.value);
  }

  const caminoHtml = camino.length
    ? '<ol class="analytics-camino">' +
      camino
        .map((step, idx) => {
          return (
            '<li class="analytics-camino-step">' +
            (idx > 0 ? '<span class="analytics-camino-arrow" aria-hidden="true">→</span>' : '') +
            '<div class="analytics-camino-card">' +
            '<p class="analytics-camino-label">' +
            esc(step.label || step.key || '') +
            '</p>' +
            '<p class="analytics-camino-value">' +
            esc(fmtCaminoValue(step)) +
            '</p></div></li>'
          );
        })
        .join('') +
      '</ol>'
    : '';

  const cards = [
    {
      title: 'Gasto Meta',
      value: fmtMoneyClp(t.spend),
      hint: 'Inversión en el período seleccionado',
      state: 'info'
    },
    {
      title: 'Leads Kommo',
      value: fmtNum(t.kommoMatched),
      hint: 'Creados en rango · match campaña',
      state: 'ok'
    },
    {
      title: 'Reuniones',
      value: fmtNum(t.meetings),
      hint: 'Atribuidas vía contacto del cohort',
      state: 'ok'
    },
    {
      title: 'Con minuta',
      value: fmtNum(t.meetingsMinuta),
      hint: 'Registro Meet en planilla',
      state: 'muted'
    },
    {
      title: '→ Propuesta / Reserva',
      value: fmtNum(t.meetingsPropuesta) + ' / ' + fmtNum(t.meetingsReserva),
      hint: 'Mismo cliente · hasta ' + windowDays + ' días después',
      state: 'ok'
    },
    {
      title: 'UF reserva (proxy)',
      value:
        t.reservaUf != null && Number(t.reservaUf) > 0
          ? Number(t.reservaUf).toLocaleString('es-CL', { maximumFractionDigits: 1 }) +
            ' UF'
          : '—',
      hint: 'Valor propiedad · no es comisión GI',
      state: 'info'
    },
    {
      title: 'Costo / reunión · / reserva',
      value:
        (t.costPerMeeting != null ? fmtMoneyClp(t.costPerMeeting) : '—') +
        ' · ' +
        (t.costPerReserva != null ? fmtMoneyClp(t.costPerReserva) : '—'),
      hint: 'Gasto Meta ÷ reuniones · ÷ reservas',
      state: 'muted'
    },
    {
      title: 'En proceso / Ganado',
      value: fmtNum(t.enProceso) + ' / ' + fmtNum(t.ganado),
      hint: 'Estado actual de la cohorte matcheada',
      state: 'ok'
    }
  ];
  const grid = cards
    .map((c) => {
      const st =
        c.state === 'ok'
          ? ' is-ok'
          : c.state === 'info'
            ? ' is-info'
            : ' is-muted';
      return (
        '<article class="analytics-kpi-card' +
        st +
        '">' +
        '<p class="analytics-kpi-label">' +
        esc(c.title) +
        '</p>' +
        '<p class="analytics-kpi-value">' +
        esc(c.value) +
        '</p>' +
        '<p class="analytics-kpi-hint">' +
        esc(c.hint) +
        '</p></article>'
      );
    })
    .join('');

  let body;
  if (!camps.length) {
    body =
      '<p class="analytics-empty" style="margin-top:12px">Sin campañas Meta con actividad en este período.</p>';
  } else {
    const rows = camps
      .map((c) => {
        return (
          '<tr>' +
          '<td><strong>' +
          esc(c.name || c.id || '—') +
          '</strong></td>' +
          '<td class="num">' +
          fmtMoneyClp(c.spend) +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.kommoLeads) +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.sinProcesar) +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.enProceso) +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.meetings) +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.meetingsMinuta) +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.meetingsPropuesta) +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.meetingsReserva) +
          '</td>' +
          '<td class="num">' +
          (c.reservaUf != null && Number(c.reservaUf) > 0
            ? Number(c.reservaUf).toLocaleString('es-CL', { maximumFractionDigits: 1 })
            : '—') +
          '</td>' +
          '<td class="num">' +
          (c.convLeadMeeting != null ? fmtNum(c.convLeadMeeting) + '%' : '—') +
          '</td>' +
          '<td class="num">' +
          (c.costPerMeeting != null ? fmtMoneyClp(c.costPerMeeting) : '—') +
          '</td>' +
          '<td class="num">' +
          (c.costPerReserva != null ? fmtMoneyClp(c.costPerReserva) : '—') +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.ganado) +
          '</td>' +
          '</tr>'
        );
      })
      .join('');
    body =
      '<div class="analytics-panel" style="margin-top:12px">' +
      '<div class="analytics-table-wrap"><table class="analytics-table"><thead><tr>' +
      '<th>Campaña</th>' +
      '<th class="num">Gasto</th>' +
      '<th class="num">Leads</th>' +
      '<th class="num">Sin proc.</th>' +
      '<th class="num">En proceso</th>' +
      '<th class="num">Reuniones</th>' +
      '<th class="num">Minuta</th>' +
      '<th class="num">Prop.</th>' +
      '<th class="num">Reserva</th>' +
      '<th class="num" title="UF de reservas vinculadas (proxy; no comisión)">UF res.</th>' +
      '<th class="num" title="Reuniones ÷ leads">Conv. reun.</th>' +
      '<th class="num" title="Gasto ÷ reuniones">CPR</th>' +
      '<th class="num" title="Gasto ÷ reservas">CPRes</th>' +
      '<th class="num">Ganado</th>' +
      '</tr></thead><tbody>' +
      rows +
      '</tbody></table></div></div>';
  }

  const sin = gastoRetorno.kommoSinCampanaMeta || {};
  let foot = '';
  if (gastoRetorno.hint) {
    foot +=
      '<p class="analytics-section-note" style="margin-top:10px;color:#b45309">' +
      esc(gastoRetorno.hint) +
      '</p>';
  }
  if (diag && (diag.cohortLeads != null || diag.withCampaignId != null)) {
    foot +=
      '<p class="analytics-section-note" style="margin-top:4px">Diagnóstico Kommo en rango: ' +
      fmtNum(diag.cohortLeads) +
      ' leads · ' +
      fmtNum(diag.withCampaignId) +
      ' Campaign ID · ' +
      fmtNum(diag.withMetaLeadId) +
      ' Meta Lead ID · ' +
      fmtNum(diag.leadAdsSignals) +
      ' señal Lead Ads · ' +
      fmtNum(diag.withCampaignName) +
      ' con nombre · ' +
      fmtNum(diag.landingTagged) +
      ' etiqueta Landing · ' +
      fmtNum(diag.metaFlagged) +
      ' flagged Meta.</p>';
  }
  if (diag && diag.meetingsTotal != null) {
    foot +=
      '<p class="analytics-section-note" style="margin-top:4px">Reuniones en rango: ' +
      fmtNum(diag.meetingsTotal) +
      ' · atribuidas ' +
      fmtNum(diag.meetingsAttributed) +
      ' · sin Kommo ' +
      fmtNum(diag.meetingsSinKommo) +
      ' · sin campaña del cohort ' +
      fmtNum(diag.meetingsSinCampana) +
      ' · contactos con campaña ' +
      fmtNum(diag.contactsWithCamp) +
      ' · ventana comercial ' +
      fmtNum(windowDays) +
      ' días.</p>';
  }
  if (Number(sin.kommoLeads) > 0 || Number(sin.meetings) > 0) {
    foot +=
      '<p class="analytics-section-note" style="margin-top:4px">' +
      fmtNum(sin.kommoLeads) +
      ' leads Kommo del período sin campaña Meta matcheada' +
      (Number(sin.meetings) > 0
        ? ' · ' + fmtNum(sin.meetings) + ' reuniones sin campaña del cohort'
        : '') +
      '.</p>';
  }

  return (
    '<div class="analytics-gasto-retorno">' +
    '<p class="analytics-section-note" style="margin:0 0 10px">Período seleccionado: <strong>' +
    esc(range) +
    '</strong> (filtro arriba). Cambiá el rango para recalcular el camino completo.' +
    (gastoRetorno.note ? ' · ' + esc(gastoRetorno.note) : '') +
    '</p>' +
    (caminoHtml
      ? '<div class="analytics-camino-wrap" aria-label="Camino del período">' +
        caminoHtml +
        '</div>'
      : '') +
    '<div class="analytics-kpi-grid">' +
    grid +
    '</div>' +
    body +
    foot +
    '</div>'
  );
}

/**
 * Camino por alianza / origen No Meta (PatagoLand, Euro, etc.) — sin CPL Meta.
 */
export function buildCaminoAlianzasHtml(caminoAlianzas) {
  if (!caminoAlianzas) {
    return '<p class="analytics-empty">Sin camino de alianzas para el período.</p>';
  }
  const t = caminoAlianzas.totals || {};
  const origins = Array.isArray(caminoAlianzas.origins) ? caminoAlianzas.origins : [];
  const diag = caminoAlianzas.diagnostics || {};
  const camino = Array.isArray(caminoAlianzas.camino) ? caminoAlianzas.camino : [];
  const windowDays = caminoAlianzas.windowDays != null ? caminoAlianzas.windowDays : 21;
  const range =
    (caminoAlianzas.dateFrom || '—') + ' → ' + (caminoAlianzas.dateTo || '—');

  function fmtStep(step) {
    if (!step) return '—';
    if (step.unit === 'note') return 'No medible';
    if (step.unit === 'uf') {
      if (step.value == null || Number.isNaN(Number(step.value))) return '—';
      return (
        Number(step.value).toLocaleString('es-CL', { maximumFractionDigits: 1 }) + ' UF'
      );
    }
    return fmtNum(step.value);
  }

  const caminoHtml = camino.length
    ? '<ol class="analytics-camino">' +
      camino
        .map((step, idx) => {
          return (
            '<li class="analytics-camino-step">' +
            (idx > 0
              ? '<span class="analytics-camino-arrow" aria-hidden="true">→</span>'
              : '') +
            '<div class="analytics-camino-card">' +
            '<p class="analytics-camino-label">' +
            esc(step.label || step.key || '') +
            '</p>' +
            '<p class="analytics-camino-value">' +
            esc(fmtStep(step)) +
            '</p></div></li>'
          );
        })
        .join('') +
      '</ol>'
    : '';

  const cards = [
    {
      title: 'Costo campaña',
      value: 'No medible',
      hint: 'Alianza · fuera de Meta Ads',
      state: 'muted'
    },
    {
      title: 'Leads alianza',
      value: fmtNum(t.kommoMatched),
      hint: 'Etiqueta No Meta · creados en el período',
      state: 'ok'
    },
    {
      title: 'Reuniones',
      value: fmtNum(t.meetings),
      hint: 'Sin contacto también Meta en el cohort',
      state: 'ok'
    },
    {
      title: '→ Propuesta / Reserva',
      value: fmtNum(t.meetingsPropuesta) + ' / ' + fmtNum(t.meetingsReserva),
      hint: 'Hasta ' + windowDays + ' días después',
      state: 'ok'
    },
    {
      title: 'UF reserva (proxy)',
      value:
        t.reservaUf != null && Number(t.reservaUf) > 0
          ? Number(t.reservaUf).toLocaleString('es-CL', { maximumFractionDigits: 1 }) +
            ' UF'
          : '—',
      hint: 'Valor propiedad · no comisión GI',
      state: 'info'
    },
    {
      title: 'Conv. lead → reunión',
      value: t.convLeadMeeting != null ? fmtNum(t.convLeadMeeting) + '%' : '—',
      hint: 'Reuniones ÷ leads alianza',
      state: 'muted'
    }
  ];
  const grid = cards
    .map((c) => {
      const st =
        c.state === 'ok'
          ? ' is-ok'
          : c.state === 'info'
            ? ' is-info'
            : ' is-muted';
      return (
        '<article class="analytics-kpi-card' +
        st +
        '">' +
        '<p class="analytics-kpi-label">' +
        esc(c.title) +
        '</p>' +
        '<p class="analytics-kpi-value">' +
        esc(c.value) +
        '</p>' +
        '<p class="analytics-kpi-hint">' +
        esc(c.hint) +
        '</p></article>'
      );
    })
    .join('');

  let body;
  if (!origins.length) {
    body =
      '<p class="analytics-empty" style="margin-top:12px">Sin leads de alianzas (orígenes No Meta) en este período. Revisá etiquetas en Config → Orígenes.</p>';
  } else {
    const rows = origins
      .map((o) => {
        return (
          '<tr>' +
          '<td><strong>' +
          esc(o.tagName || '—') +
          '</strong>' +
          '<div class="analytics-section-note" style="margin:2px 0 0">Sin costo Meta</div></td>' +
          '<td class="num">' +
          fmtNum(o.kommoLeads) +
          '</td>' +
          '<td class="num">' +
          fmtNum(o.sinProcesar) +
          '</td>' +
          '<td class="num">' +
          fmtNum(o.enProceso) +
          '</td>' +
          '<td class="num">' +
          fmtNum(o.meetings) +
          '</td>' +
          '<td class="num">' +
          fmtNum(o.meetingsMinuta) +
          '</td>' +
          '<td class="num">' +
          fmtNum(o.meetingsPropuesta) +
          '</td>' +
          '<td class="num">' +
          fmtNum(o.meetingsReserva) +
          '</td>' +
          '<td class="num">' +
          (o.reservaUf != null && Number(o.reservaUf) > 0
            ? Number(o.reservaUf).toLocaleString('es-CL', { maximumFractionDigits: 1 })
            : '—') +
          '</td>' +
          '<td class="num">' +
          (o.convLeadMeeting != null ? fmtNum(o.convLeadMeeting) + '%' : '—') +
          '</td>' +
          '<td class="num">' +
          fmtNum(o.ganado) +
          '</td>' +
          '</tr>'
        );
      })
      .join('');
    body =
      '<div class="analytics-panel" style="margin-top:12px">' +
      '<div class="analytics-table-wrap"><table class="analytics-table"><thead><tr>' +
      '<th>Alianza / origen</th>' +
      '<th class="num">Leads</th>' +
      '<th class="num">Sin proc.</th>' +
      '<th class="num">En proceso</th>' +
      '<th class="num">Reuniones</th>' +
      '<th class="num">Minuta</th>' +
      '<th class="num">Prop.</th>' +
      '<th class="num">Reserva</th>' +
      '<th class="num">UF res.</th>' +
      '<th class="num">Conv. reun.</th>' +
      '<th class="num">Ganado</th>' +
      '</tr></thead><tbody>' +
      rows +
      '</tbody></table></div></div>';
  }

  let foot = '';
  if (diag && (diag.unclassifiedLeads > 0 || diag.noneLeads > 0)) {
    foot +=
      '<p class="analytics-section-note" style="margin-top:10px">' +
      fmtNum(diag.unclassifiedLeads) +
      ' leads con etiqueta sin clasificar · ' +
      fmtNum(diag.noneLeads) +
      ' sin etiqueta (no entran a alianzas hasta clasificarlas en Config → Orígenes).</p>';
  }
  if (diag && diag.meetingsTotal != null) {
    foot +=
      '<p class="analytics-section-note" style="margin-top:4px">Reuniones: atribuidas a alianza ' +
      fmtNum(diag.meetingsAttributed) +
      ' · omitidas por contacto Meta ' +
      fmtNum(diag.meetingsSkippedMeta) +
      ' · sin origen alianza ' +
      fmtNum(diag.meetingsSinOrigen) +
      '.</p>';
  }

  return (
    '<div class="analytics-camino-alianzas">' +
    '<p class="analytics-section-note" style="margin:0 0 10px">Período: <strong>' +
    esc(range) +
    '</strong>. PatagoLand, Euro y otras etiquetas No Meta: mismo camino, sin CPL.' +
    (caminoAlianzas.note ? ' · ' + esc(caminoAlianzas.note) : '') +
    '</p>' +
    (caminoHtml
      ? '<div class="analytics-camino-wrap" aria-label="Camino alianzas">' +
        caminoHtml +
        '</div>'
      : '') +
    '<div class="analytics-kpi-grid">' +
    grid +
    '</div>' +
    body +
    foot +
    '</div>'
  );
}

/**
 * Mapa empírico: etiqueta Kommo ↔ campaña Meta (período).
 */
export function buildTagCampaignMapHtml(tagCampaignMap) {
  if (!tagCampaignMap) {
    return '<p class="analytics-empty">Sin mapa etiqueta↔campaña para el período.</p>';
  }
  const byCamp = Array.isArray(tagCampaignMap.byCampaign)
    ? tagCampaignMap.byCampaign
    : [];
  const byTag = Array.isArray(tagCampaignMap.byTag) ? tagCampaignMap.byTag : [];
  const diag = tagCampaignMap.diagnostics || {};
  const range =
    (tagCampaignMap.dateFrom || '—') + ' → ' + (tagCampaignMap.dateTo || '—');

  function classLabel(c) {
    if (c === 'meta') return 'Meta';
    if (c === 'other') return 'No Meta';
    if (c === 'unclassified') return 'Sin clasificar';
    if (c === 'not_origin') return 'No origen';
    return '—';
  }

  let campBody;
  if (!byCamp.length) {
    campBody =
      '<p class="analytics-empty">Ninguna campaña Meta con leads Kommo matcheados en este período.</p>';
  } else {
    const rows = byCamp
      .map((c) => {
        const tags = Array.isArray(c.tags) ? c.tags : [];
        const tagCell = tags.length
          ? tags
              .slice(0, 8)
              .map(
                (t) =>
                  esc(t.tagName) +
                  ' <span class="analytics-muted">(' +
                  fmtNum(t.count) +
                  ' · ' +
                  esc(classLabel(t.originClass)) +
                  ')</span>'
              )
              .join('<br>') +
            (tags.length > 8
              ? '<br><span class="analytics-muted">+' + (tags.length - 8) + ' más</span>'
              : '')
          : '<span class="analytics-muted">Sin etiquetas en esos leads</span>';
        return (
          '<tr>' +
          '<td><strong>' +
          esc(c.name || c.id || '—') +
          '</strong>' +
          (c.id
            ? '<div class="analytics-section-note" style="margin:2px 0 0">ID ' +
              esc(c.id) +
              '</div>'
            : '') +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.leadCount) +
          '</td>' +
          '<td>' +
          tagCell +
          '</td>' +
          '</tr>'
        );
      })
      .join('');
    campBody =
      '<div class="analytics-panel" style="margin-top:8px">' +
      '<p class="analytics-section-note" style="margin:0 0 8px"><strong>Por campaña Meta</strong> → etiquetas Kommo vistas en sus leads</p>' +
      '<div class="analytics-table-wrap"><table class="analytics-table"><thead><tr>' +
      '<th>Campaña Meta</th>' +
      '<th class="num">Leads match</th>' +
      '<th>Etiquetas Kommo (en esos leads)</th>' +
      '</tr></thead><tbody>' +
      rows +
      '</tbody></table></div></div>';
  }

  let tagBody;
  const tagsWithCamp = byTag.filter((t) => Number(t.withCampaign) > 0);
  if (!tagsWithCamp.length) {
    tagBody =
      '<p class="analytics-empty" style="margin-top:12px">Ninguna etiqueta aparece junto a un match de campaña en este período.</p>';
  } else {
    const rows = tagsWithCamp
      .slice(0, 40)
      .map((t) => {
        const camps = Array.isArray(t.campaigns) ? t.campaigns : [];
        const campCell = camps.length
          ? camps
              .slice(0, 4)
              .map(
                (c) =>
                  esc(c.name || c.id || '—') +
                  ' <span class="analytics-muted">(' +
                  fmtNum(c.count) +
                  ')</span>'
              )
              .join('<br>')
          : '—';
        return (
          '<tr>' +
          '<td><strong>' +
          esc(t.tagName) +
          '</strong>' +
          '<div class="analytics-section-note" style="margin:2px 0 0">' +
          esc(classLabel(t.originClass)) +
          '</div></td>' +
          '<td class="num">' +
          fmtNum(t.leads) +
          '</td>' +
          '<td class="num">' +
          fmtNum(t.withCampaign) +
          '</td>' +
          '<td>' +
          esc(t.primaryCampaignName || '—') +
          (t.primaryShare != null
            ? ' <span class="analytics-muted">(' + fmtNum(t.primaryShare) + '%)</span>'
            : '') +
          '</td>' +
          '<td>' +
          campCell +
          '</td>' +
          '</tr>'
        );
      })
      .join('');
    tagBody =
      '<div class="analytics-panel" style="margin-top:14px">' +
      '<p class="analytics-section-note" style="margin:0 0 8px"><strong>Por etiqueta Kommo</strong> → campaña Meta más frecuente (y otras)</p>' +
      '<div class="analytics-table-wrap"><table class="analytics-table"><thead><tr>' +
      '<th>Etiqueta</th>' +
      '<th class="num">Leads</th>' +
      '<th class="num">Con campaña</th>' +
      '<th>Campaña principal</th>' +
      '<th>Otras campañas</th>' +
      '</tr></thead><tbody>' +
      rows +
      '</tbody></table></div></div>';
  }

  let foot =
    '<p class="analytics-section-note" style="margin-top:10px">Período <strong>' +
    esc(range) +
    '</strong>' +
    (tagCampaignMap.note ? ' · ' + esc(tagCampaignMap.note) : '') +
    ' · Para fijar etiqueta → campaña: Config → Orígenes.' +
    '</p>';
  if (diag && diag.cohortLeads != null) {
    foot +=
      '<p class="analytics-section-note" style="margin-top:4px">Diagnóstico: ' +
      fmtNum(diag.cohortLeads) +
      ' leads · ' +
      fmtNum(diag.leadsMatched) +
      ' match campaña · ' +
      fmtNum(diag.leadsMatchedNoTags) +
      ' match sin etiqueta · ' +
      fmtNum(diag.leadsUnmatchedWithTags) +
      ' con etiqueta sin match · ' +
      fmtNum(diag.distinctTags) +
      ' etiquetas distintas.</p>';
  }

  return (
    '<div class="analytics-tag-campaign-map">' + foot + campBody + tagBody + '</div>'
  );
}

/**
 * Comparativa período: KPIs Kommo vs Meta (resumen).
 */
/**
 * Vías de creación en Kommo (Meta Lead Ads primero; luego Landing / Contacto / Patagon / Manual).
 */
export function buildEntryChannelsHtml(entryChannels) {
  if (!entryChannels) {
    return '<p class="analytics-empty">Sin datos de vía de entrada para el período.</p>';
  }
  const total = Number(entryChannels.total) || 0;
  const rawChannels = Array.isArray(entryChannels.channels) ? entryChannels.channels : [];
  if (!total && !rawChannels.some((c) => Number(c.count) > 0)) {
    return (
      '<p class="analytics-empty">No hay leads Kommo creados en este rango para clasificar por vía.</p>'
    );
  }

  const DISPLAY_ORDER = [
    'meta_lead_ads',
    'landing_web',
    'contacto_web',
    'patagonland',
    'manual_u_otro'
  ];
  const byId = {};
  rawChannels.forEach((c) => {
    if (c && c.id) byId[c.id] = c;
  });
  const channels = DISPLAY_ORDER.map((id) => byId[id]).filter(Boolean);
  rawChannels.forEach((c) => {
    if (c && c.id && !DISPLAY_ORDER.includes(c.id)) channels.push(c);
  });

  function channelBlockHtml(c) {
    const share =
      c.share != null
        ? c.share
        : total
          ? Math.round((Number(c.count) / total) * 1000) / 10
          : 0;
    const main =
      '<tr class="analytics-entry-channel-row' +
      (c.id === 'meta_lead_ads' ? ' analytics-entry-channel-meta' : '') +
      '">' +
      '<td><strong>' +
      esc(c.label || c.id || '—') +
      '</strong></td>' +
      '<td class="num">' +
      fmtNum(c.count) +
      '</td>' +
      '<td class="num">' +
      fmtNum(share) +
      '%</td>' +
      '<td class="num">' +
      fmtNum(c.sinProcesar) +
      '</td>' +
      '<td class="num">' +
      fmtNum(c.enProceso) +
      '</td>' +
      '<td class="num">' +
      fmtNum(c.terminado) +
      '</td>' +
      '<td class="num">' +
      fmtNum(c.ganado) +
      '</td>' +
      '<td class="num">' +
      fmtNum(c.perdido) +
      '</td>' +
      '<td class="num">' +
      fmtNum(c.noAplica) +
      '</td>' +
      '</tr>';
    const camps = Array.isArray(c.campaigns) ? c.campaigns : [];
    if (!camps.length || !Number(c.count)) return main;
    const sub = camps
      .map((camp) => {
        return (
          '<tr class="analytics-entry-campaign-row">' +
          '<td class="analytics-entry-campaign-label">' +
          '<span class="analytics-muted">↳ campaña</span> ' +
          esc(camp.label || camp.campaignName || camp.campaignId || '—') +
          (camp.shareOfChannel != null
            ? ' <span class="analytics-muted">(' +
              fmtNum(camp.shareOfChannel) +
              '% vía)</span>'
            : '') +
          '</td>' +
          '<td class="num">' +
          fmtNum(camp.count) +
          '</td>' +
          '<td class="num analytics-muted">—</td>' +
          '<td class="num">' +
          fmtNum(camp.sinProcesar) +
          '</td>' +
          '<td class="num">' +
          fmtNum(camp.enProceso) +
          '</td>' +
          '<td class="num">' +
          fmtNum(camp.terminado) +
          '</td>' +
          '<td class="num">' +
          fmtNum(camp.ganado) +
          '</td>' +
          '<td class="num">' +
          fmtNum(camp.perdido) +
          '</td>' +
          '<td class="num">' +
          fmtNum(camp.noAplica) +
          '</td>' +
          '</tr>'
        );
      })
      .join('');
    return main + sub;
  }

  let rows = '';
  let sepInserted = false;
  channels.forEach((c) => {
    const isMeta = c.id === 'meta_lead_ads';
    if (!isMeta && !sepInserted) {
      const hasMeta = channels.some((x) => x.id === 'meta_lead_ads');
      if (hasMeta) {
        rows +=
          '<tr class="analytics-entry-channels-sep" aria-hidden="true">' +
          '<td colspan="9"><span class="analytics-entry-channels-sep-label">Otras vías</span></td>' +
          '</tr>';
      }
      sepInserted = true;
    }
    rows += channelBlockHtml(c);
  });

  return (
    '<div class="analytics-table-wrap">' +
    '<table class="analytics-table analytics-entry-channels-table">' +
    '<thead><tr>' +
    '<th>Vía</th><th class="num">Leads</th><th class="num">%</th>' +
    '<th class="num">Sin proc.</th><th class="num">En proc.</th><th class="num">Term.</th>' +
    '<th class="num">Ganado</th><th class="num">Perdido</th><th class="num">No aplica</th>' +
    '</tr></thead>' +
    '<tbody>' +
    rows +
    '</tbody>' +
    '<tfoot><tr>' +
    '<td><strong>Total</strong></td>' +
    '<td class="num"><strong>' +
    fmtNum(total) +
    '</strong></td>' +
    '<td class="num">100%</td>' +
    '<td colspan="6"></td>' +
    '</tr></tfoot>' +
    '</table></div>' +
    (entryChannels.audit
      ? '<p class="analytics-section-note" style="margin-top:8px">' +
        esc(
          'Auditoría En proc.: Landing ' +
            fmtNum(entryChannels.audit.landingEnProceso) +
            ' · Meta ' +
            fmtNum(entryChannels.audit.metaEnProceso) +
            ' · solape IDs ' +
            fmtNum(entryChannels.audit.overlapEnProceso) +
            ' — ' +
            (entryChannels.audit.note || '')
        ) +
        '</p>'
      : '') +
    channels
      .filter((c) => Number(c.enProceso) > 0 && Array.isArray(c.cicloBreakdown))
      .map((c) => {
        const bits = c.cicloBreakdown
          .filter((r) => r.ciclo === 'en_proceso')
          .slice(0, 8)
          .map(
            (r) =>
              esc(r.internalStateName || r.internalStateId || '—') +
              ' · ' +
              esc(r.stageName || '—') +
              ' (' +
              fmtNum(r.count) +
              ')'
          );
        if (!bits.length) return '';
        return (
          '<p class="analytics-section-note" style="margin-top:4px">' +
          '<strong>' +
          esc(c.label) +
          ' · en proceso:</strong> ' +
          bits.join(' · ') +
          '</p>'
        );
      })
      .join('') +
    (entryChannels.note
      ? '<p class="analytics-section-note" style="margin-top:8px">' +
        esc(entryChannels.note) +
        '</p>'
      : '')
  );
}

export function buildPeriodSourcesHtml(periodSources) {
  if (!periodSources) {
    return '<p class="analytics-empty">Sin comparativa Kommo / Meta para el período.</p>';
  }
  const range =
    (periodSources.dateFrom || '—') + ' → ' + (periodSources.dateTo || '—');
  const k = periodSources.kommo || {};
  const m = periodSources.meta || {};
  const campaigns = Array.isArray(m.campaigns) ? m.campaigns : [];
  const metaVal =
    m.ok === false
      ? 'Sin datos'
      : m.leads != null
        ? fmtNum(m.leads)
        : 'Sin datos';
  const metaHint =
    m.ok === false
      ? m.error || 'Meta Ads no disponible'
      : m.spend != null
        ? 'Gasto ' + fmtMoneyClp(m.spend)
        : m.source || 'Meta Marketing API';
  const cards = [
    {
      title: 'Kommo · creados',
      value: fmtNum(k.createdInPeriod),
      hint: k.source || 'Creado at',
      tip: 'Leads con fecha de creación en el rango (tras filtro Orígenes).',
      state: 'info'
    },
    {
      title: 'Kommo · origen Meta',
      value: fmtNum(k.metaOrigin),
      hint: 'De esos creados, etiqueta Meta',
      tip: 'Subconjunto de la cohorte Kommo con origen resuelto Meta.',
      state: 'ok'
    },
    {
      title: 'Kommo · No Meta',
      value: fmtNum(k.otherOrigin),
      hint: 'Otras etiquetas origen',
      tip: 'Cohorte Kommo con origen No Meta.',
      state: 'muted'
    },
    {
      title: 'Meta Ads · leads',
      value: metaVal,
      hint: metaHint,
      tip:
        'Suma de leads por campaña (acciones lead Meta) en el mismo rango. Equivalente a Resultados en Ads Manager.',
      state: m.ok === false ? 'warn' : 'info'
    }
  ];
  const grid = cards
    .map((c) => {
      const st =
        c.state === 'bad'
          ? ' is-bad'
          : c.state === 'warn'
            ? ' is-warn'
            : c.state === 'ok'
              ? ' is-ok'
              : c.state === 'info'
                ? ' is-info'
                : ' is-muted';
      return (
        '<article class="analytics-kpi-card' +
        st +
        '">' +
        '<p class="analytics-kpi-label" title="' +
        esc(c.tip) +
        '">' +
        esc(c.title) +
        '</p>' +
        '<p class="analytics-kpi-value">' +
        esc(c.value) +
        '</p>' +
        '<p class="analytics-kpi-hint">' +
        esc(c.hint) +
        '</p></article>'
      );
    })
    .join('');

  let campTable = '';
  if (m.ok === false) {
    campTable =
      '<p class="analytics-empty" style="margin-top:12px">' +
      esc(m.error || 'No se pudieron cargar campañas Meta.') +
      '</p>';
  } else if (!campaigns.length) {
    campTable =
      '<p class="analytics-empty" style="margin-top:12px">Sin campañas Meta con actividad en este período.</p>';
  } else {
    const rows = campaigns
      .map((c) => {
        return (
          '<tr>' +
          '<td><strong>' +
          esc(c.name || c.id || '—') +
          '</strong></td>' +
          '<td class="num">' +
          fmtNum(c.leads) +
          '</td>' +
          '<td class="num">' +
          (c.cpl != null ? fmtMoneyClp(c.cpl) : '—') +
          '</td>' +
          '<td class="num">' +
          fmtMoneyClp(c.spend) +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.impressions) +
          '</td>' +
          '</tr>'
        );
      })
      .join('');
    campTable =
      '<div class="analytics-panel" style="margin-top:12px">' +
      '<p class="analytics-sub" style="margin:0 0 8px">Campañas Meta (Ads Manager · resultados = clientes potenciales)</p>' +
      '<div class="analytics-table-wrap"><table class="analytics-table"><thead><tr>' +
      '<th>Campaña</th>' +
      '<th class="num">Leads</th>' +
      '<th class="num">CPL</th>' +
      '<th class="num">Gasto</th>' +
      '<th class="num">Impresiones</th>' +
      '</tr></thead><tbody>' +
      rows +
      '</tbody></table></div></div>';
  }

  return (
    '<div class="analytics-period-sources">' +
    '<p class="analytics-section-note" style="margin:0 0 10px">Período <strong>' +
    esc(range) +
    '</strong>' +
    (periodSources.note ? ' · ' + esc(periodSources.note) : '') +
    '</p>' +
    '<div class="analytics-kpi-grid">' +
    grid +
    '</div>' +
    campTable +
    '</div>'
  );
}

/**
 * Foto de orígenes: Meta por campaña Ads; alianzas por etiqueta Kommo.
 * Expandible → estado interno → Embudo→Etapa.
 */
export function buildOriginsHtml(origins) {
  if (!origins) {
    return (
      '<p class="analytics-empty">Sin datos de origen. Clasificá etiquetas en Configuración → Orígenes.</p>'
    );
  }
  const openTotal = Number(origins.openTotal) || 0;
  const meta = origins.meta || { count: 0, tags: [], campaigns: [] };
  const other = Array.isArray(origins.other) ? origins.other : [];
  const unclassifiedDetail = origins.unclassifiedDetail || null;
  const noneDetail = origins.noneDetail || null;
  const isPeriod = !!origins.periodDependent;
  const liveChip = isPeriod ? 'Período' : 'Foto en vivo';
  const liveNote = isPeriod
    ? 'Leads creados en el rango · estado actual'
    : 'Stock abierto ahora';

  const kommoStageRows = (stages, parentCount) => {
    const list = Array.isArray(stages) ? stages : [];
    if (!list.length) {
      return (
        '<tr class="analytics-kommo-empty"><td colspan="4"><span class="analytics-sub">Sin etapas Kommo.</span></td></tr>'
      );
    }
    return list
      .map((st) => {
        const pct =
          st.share != null
            ? st.share
            : parentCount
              ? Math.round((st.count / parentCount) * 1000) / 10
              : 0;
        return (
          '<tr class="analytics-kommo-row">' +
          '<td><span class="analytics-kommo-pipe">' +
          esc(st.pipelineName || st.pipelineId || '—') +
          '</span><span class="analytics-sub">' +
          esc(st.stageName || st.stageId || '—') +
          '</span></td>' +
          '<td class="num">' +
          fmtNum(st.count) +
          '</td>' +
          '<td class="num">' +
          fmtNum(pct) +
          '%</td>' +
          '<td class="num">' +
          (st.medianAgeDays != null ? fmtNum(st.medianAgeDays) + ' d' : '—') +
          '</td></tr>'
        );
      })
      .join('');
  };

  const cicloLabel = (c) => {
    if (c === 'en_proceso') return 'En proceso';
    if (c === 'terminado') return 'Terminado';
    return 'Sin procesar';
  };

  const stateRowsHtml = (byState, parentCount) => {
    const list = Array.isArray(byState) ? byState : [];
    if (!list.length) {
      return (
        '<tr><td colspan="4" class="analytics-empty" style="padding:8px">Sin desglose.</td></tr>'
      );
    }
    return list
      .map((s) => {
        const stages = Array.isArray(s.byKommoStage) ? s.byKommoStage : [];
        const canExpand = stages.length > 0;
        const name = esc(s.internalStateName || s.internalStateId || '—');
        const ciclo = s.ciclo || 'sin_procesar';
        const labelCell = canExpand
          ? '<details class="analytics-expand">' +
            '<summary><span class="analytics-expand-name">' +
            '<span class="analytics-ciclo-badge is-' +
            esc(ciclo) +
            '">' +
            esc(cicloLabel(ciclo)) +
            '</span> <strong>' +
            name +
            '</strong></span>' +
            '<span class="analytics-expand-hint">Ver ' +
            fmtNum(stages.length) +
            ' etapa(s) Kommo</span></summary></details>'
          : '<span class="analytics-ciclo-badge is-' +
            esc(ciclo) +
            '">' +
            esc(cicloLabel(ciclo)) +
            '</span> <strong>' +
            name +
            '</strong>';
        const main =
          '<tr class="analytics-state-row">' +
          '<td>' +
          labelCell +
          '</td>' +
          '<td class="num">' +
          fmtNum(s.count) +
          '</td>' +
          '<td class="num">' +
          (s.medianAgeDays != null ? fmtNum(s.medianAgeDays) + ' d' : '—') +
          '</td>' +
          '<td class="num">' +
          (parentCount ? fmtNum(Math.round((s.count / parentCount) * 1000) / 10) + '%' : '—') +
          '</td></tr>';
        if (!canExpand) return main;
        return (
          main +
          '<tr class="analytics-kommo-wrap"><td colspan="4">' +
          '<div class="analytics-kommo-panel">' +
          '<table class="analytics-table analytics-kommo-table"><thead><tr>' +
          '<th>Embudo → Etapa (Kommo)</th><th class="num">Leads</th><th class="num">%</th><th class="num">Antig. mediana</th>' +
          '</tr></thead><tbody>' +
          kommoStageRows(stages, s.count) +
          '</tbody></table></div></td></tr>'
        );
      })
      .join('');
  };

  const tagRowsHtml = (tags, kind) => {
    const list = Array.isArray(tags) ? tags : [];
    if (!list.length) {
      return (
        '<tr><td colspan="4" class="analytics-empty" style="padding:8px">Sin leads en este grupo.</td></tr>'
      );
    }
    const badgeCls = kind === 'meta' ? 'is-meta' : kind === 'other' ? 'is-other' : 'is-muted';
    const badgeTxt = kind === 'meta' ? 'Meta' : kind === 'other' ? 'No Meta' : '—';
    return list
      .map((t) => {
        const states = Array.isArray(t.byState) ? t.byState : [];
        const canExpand = states.length > 0;
        const name = esc(t.tagName || '—');
        const labelCell = canExpand
          ? '<details class="analytics-expand">' +
            '<summary><span class="analytics-expand-name">' +
            '<span class="analytics-origin-badge ' +
            badgeCls +
            '">' +
            badgeTxt +
            '</span> <strong>' +
            name +
            '</strong></span>' +
            '<span class="analytics-expand-hint">Ver ' +
            fmtNum(states.length) +
            ' estado(s) interno(s)</span></summary></details>'
          : '<span class="analytics-origin-badge ' +
            badgeCls +
            '">' +
            badgeTxt +
            '</span> <strong>' +
            name +
            '</strong>';
        const main =
          '<tr class="analytics-state-row analytics-origin-row">' +
          '<td>' +
          labelCell +
          '</td>' +
          '<td class="num">' +
          fmtNum(t.count) +
          '</td>' +
          '<td class="num">' +
          (t.medianAgeDays != null ? fmtNum(t.medianAgeDays) + ' d' : '—') +
          '</td>' +
          '<td class="num">' +
          (openTotal ? fmtNum(Math.round((t.count / openTotal) * 1000) / 10) + '%' : '—') +
          '</td></tr>';
        if (!canExpand) return main;
        return (
          main +
          '<tr class="analytics-kommo-wrap"><td colspan="4">' +
          '<div class="analytics-kommo-panel">' +
          '<table class="analytics-table analytics-commercial-table"><thead><tr>' +
          '<th>Estado interno</th><th class="num">Leads</th><th class="num">Antig. mediana</th><th class="num">%</th>' +
          '</tr></thead><tbody>' +
          stateRowsHtml(states, t.count) +
          '</tbody></table></div></td></tr>'
        );
      })
      .join('');
  };

  const campaignRowsHtml = (campaigns) => {
    const list = Array.isArray(campaigns) ? campaigns : [];
    if (!list.length) {
      return (
        '<tr><td colspan="4" class="analytics-empty" style="padding:8px">Sin campañas identificadas. Mapeá etiqueta → campaña en Config → Orígenes.</td></tr>'
      );
    }
    return list
      .map((c) => {
        const states = Array.isArray(c.byState) ? c.byState : [];
        const canExpand = states.length > 0;
        const name = esc(c.name || c.id || '—');
        const tagBits = (Array.isArray(c.tags) ? c.tags : [])
          .slice(0, 3)
          .map((t) => esc(t.tagName) + ' (' + fmtNum(t.count) + ')')
          .join(' · ');
        const idHint = c.id ? '<span class="analytics-sub">ID ' + esc(c.id) + '</span>' : '';
        const tagHint = tagBits
          ? '<span class="analytics-sub">Tags: ' + tagBits + '</span>'
          : '';
        const labelCell = canExpand
          ? '<details class="analytics-expand">' +
            '<summary><span class="analytics-expand-name">' +
            '<span class="analytics-origin-badge is-meta">Campaña</span> <strong>' +
            name +
            '</strong></span>' +
            '<span class="analytics-expand-hint">Ver ' +
            fmtNum(states.length) +
            ' estado(s) interno(s)</span></summary></details>' +
            idHint +
            tagHint
          : '<span class="analytics-origin-badge is-meta">Campaña</span> <strong>' +
            name +
            '</strong>' +
            idHint +
            tagHint;
        const main =
          '<tr class="analytics-state-row analytics-origin-row">' +
          '<td>' +
          labelCell +
          '</td>' +
          '<td class="num">' +
          fmtNum(c.count) +
          '</td>' +
          '<td class="num">' +
          (c.medianAgeDays != null ? fmtNum(c.medianAgeDays) + ' d' : '—') +
          '</td>' +
          '<td class="num">' +
          (openTotal ? fmtNum(Math.round((c.count / openTotal) * 1000) / 10) + '%' : '—') +
          '</td></tr>';
        if (!canExpand) return main;
        return (
          main +
          '<tr class="analytics-kommo-wrap"><td colspan="4">' +
          '<div class="analytics-kommo-panel">' +
          '<table class="analytics-table analytics-commercial-table"><thead><tr>' +
          '<th>Estado interno</th><th class="num">Leads</th><th class="num">Antig. mediana</th><th class="num">%</th>' +
          '</tr></thead><tbody>' +
          stateRowsHtml(states, c.count) +
          '</tbody></table></div></td></tr>'
        );
      })
      .join('');
  };

  const originCard = (opts) => {
    const useCampaigns = opts.kind === 'meta' && Array.isArray(opts.campaigns);
    const rows = useCampaigns
      ? campaignRowsHtml(opts.campaigns)
      : tagRowsHtml(opts.tags || [], opts.kind);
    const colLabel = useCampaigns ? 'Campaña Meta (lo que pago)' : 'Etiqueta (origen)';
    const hint = useCampaigns
      ? 'Tocá una campaña para ver estado interno → Embudo/Etapa'
      : 'Tocá una etiqueta para ver estado interno → Embudo/Etapa';
    const total = Number(opts.total) || 0;
    return (
      '<article class="analytics-panel analytics-cycle-card analytics-origin-card is-' +
      esc(opts.kind) +
      '">' +
      '<header class="analytics-cycle-head">' +
      '<span class="analytics-cycle-total">' +
      fmtNum(total) +
      '</span>' +
      '<span class="analytics-cycle-titlewrap">' +
      '<span class="analytics-origin-badge ' +
      (opts.kind === 'meta' ? 'is-meta' : opts.kind === 'other' ? 'is-other' : 'is-muted') +
      '">' +
      esc(opts.label) +
      '</span>' +
      '<span class="analytics-chip is-live">' +
      esc(liveChip) +
      '</span>' +
      '</span></header>' +
      '<p class="analytics-sub" style="margin:2px 0 8px">' +
      esc(opts.note) +
      ' · ' +
      esc(hint) +
      '</p>' +
      '<table class="analytics-table analytics-commercial-table"><thead><tr>' +
      '<th>' +
      esc(colLabel) +
      '</th><th class="num">Leads</th><th class="num">Antig. mediana</th><th class="num">%</th>' +
      '</tr></thead><tbody>' +
      rows +
      '</tbody></table></article>'
    );
  };

  const metaCampaigns = Array.isArray(meta.campaigns) ? meta.campaigns : [];
  const metaTags = Array.isArray(meta.tags) ? meta.tags : [];
  const extra = [];
  if (unclassifiedDetail && unclassifiedDetail.count > 0) extra.push(unclassifiedDetail);
  if (noneDetail && noneDetail.count > 0) extra.push(noneDetail);

  if (!metaCampaigns.length && !metaTags.length && !other.length && !extra.length) {
    return '<p class="analytics-empty">Sin leads abiertos para desglosar por origen.</p>';
  }

  return (
    '<div class="analytics-origins-summary">' +
    '<div class="analytics-origins-kpis">' +
    '<span class="analytics-chip is-live">' +
    esc(liveChip) +
    ' · ' +
    esc(liveNote) +
    ' · ' +
    fmtNum(openTotal) +
    ' leads</span></div>' +
    '<div class="analytics-commercial-grid">' +
    originCard({
      kind: 'meta',
      label: 'Meta · por campaña',
      total: meta.count,
      campaigns: metaCampaigns.length ? metaCampaigns : null,
      tags: metaTags,
      note: 'Inversión Ads: stock atribuido a cada campaña (Campaign ID / mapa Config)'
    }) +
    originCard({
      kind: 'other',
      label: 'Alianzas (No Meta)',
      total: other.reduce((s, t) => s + (Number(t.count) || 0), 0),
      tags: other,
      note: 'Sin CPL Meta · cada etiqueta es un origen aparte'
    }) +
    (extra.length
      ? originCard({
          kind: 'muted',
          label: 'Sin clasificar / sin etiqueta',
          total: extra.reduce((s, t) => s + (Number(t.count) || 0), 0),
          tags: extra,
          note: 'Clasificá en Configuración → Orígenes (y mapa a campaña si es Meta)'
        })
      : '') +
    '</div>' +
    '<p class="analytics-section-note">Meta se lee por <strong>campaña</strong> (lo pagado). Alianzas por etiqueta. Si falta campaña: Config → Orígenes (etiqueta → Campaign ID) o sincronizá Kommo con fields de Meta.</p>' +
    '</div>'
  );
}

/**
 * Cola crítica por ciclo:
 *  · Sin procesar demorado (> umbral días sin gestión).
 *  · En proceso estancado (> umbral días).
 * Terminado no entra. No usa SLA ni notas.
 */
export function buildCriticalByCicloHtml(attention, opts) {
  const options = opts || {};
  const expanded = !!options.expanded;
  const preview =
    options.previewSize != null
      ? Number(options.previewSize)
      : ANALYTICS_CONFIG.attentionPreviewSize || 15;
  const total = attention && attention.total != null ? Number(attention.total) : 0;
  const items = (attention && attention.items) || [];
  const byC = (attention && attention.byCiclo) || {};

  const bits = [];
  if (Number(byC.sin_procesar) > 0) {
    bits.push(fmtNum(byC.sin_procesar) + ' sin procesar demorados');
  }
  if (Number(byC.en_proceso) > 0) {
    bits.push(fmtNum(byC.en_proceso) + ' en proceso estancados');
  }

  const summary =
    '<div class="analytics-critical-card ' +
    (total ? 'is-urgent' : 'is-ok') +
    '">' +
    '<p class="analytics-critical-value">' +
    (total ? fmtNum(total) + ' leads requieren atención' : 'Sin backlog crítico') +
    '</p>' +
    '<p class="analytics-critical-sub">' +
    esc(bits.length ? bits.join(' · ') + '.' : 'Nada demorado ni estancado según tus umbrales.') +
    '</p>' +
    '</div>';

  if (!items.length) return summary;

  const show = expanded ? items : items.slice(0, preview);
  const canExpand = items.length > preview || total > preview;

  const head =
    '<div class="analytics-attention-head"' +
    (canExpand ? ' data-expand-attention="1"' : '') +
    '>' +
    '<p class="analytics-section-note" style="margin:0">Mostrando ' +
    show.length +
    ' de ' +
    fmtNum(total) +
    ' críticos</p>' +
    (canExpand
      ? '<button type="button" class="analytics-btn analytics-btn-ghost" data-attention-toggle="1">' +
        (expanded ? 'Ver menos' : 'Ver todos los críticos') +
        '</button>'
      : '') +
    '</div>';

  const rows = show
    .map((a) => {
      const lab = sellerLabel(a.sellerId, null);
      const url = kommoLeadUrl(a.leadId);
      return (
        '<tr><td><strong>' +
        esc(a.name || 'Lead') +
        '</strong><span class="analytics-sub">' +
        esc(a.leadId || '') +
        '</span></td><td>' +
        cicloBadge(a.ciclo) +
        (a.internalStateName
          ? '<span class="analytics-sub">' + esc(a.internalStateName) + '</span>'
          : '') +
        '</td><td>' +
        esc(a.stageName || '—') +
        '</td><td>' +
        esc(lab.title) +
        (lab.sub ? '<span class="analytics-sub">' + esc(lab.sub) + '</span>' : '') +
        '</td><td class="num">' +
        (a.ageDaysSinceCreation != null ? fmtNum(a.ageDaysSinceCreation) + ' d' : '—') +
        (a.critDays != null
          ? '<span class="analytics-sub">umbral > ' + fmtNum(a.critDays) + ' d</span>'
          : '') +
        '</td><td>' +
        (url
          ? '<a class="analytics-link-ext" href="' +
            esc(url) +
            '" target="_blank" rel="noopener noreferrer">Abrir en Kommo</a>'
          : '<span class="analytics-sub">—</span>') +
        '</td></tr>'
      );
    })
    .join('');

  const table =
    '<div class="analytics-panel-scroll"><table class="analytics-table"><thead><tr>' +
    '<th>Lead</th><th>Ciclo · estado</th><th>Etapa (Kommo)</th><th>Vendedor</th>' +
    '<th class="num">Antigüedad</th><th>Acción</th>' +
    '</tr></thead><tbody>' +
    rows +
    '</tbody></table></div>';

  return summary + head + table;
}

export function buildSnapshotHtml(snapshot) {
  if (!snapshot) {
    return '<p class="analytics-empty">Sin datos.</p>';
  }
  const total = Number(snapshot.totalOpen) || 0;
  const th = snapshot.thresholds || { staleDays: 7, oldDays: 15 };
  const tiles = [
    { label: 'Abiertos ahora', value: total, cls: '' },
    { label: 'Sin procesar', value: snapshot.sinProcesar, cls: '' },
    { label: 'En proceso', value: snapshot.enProceso, cls: '' },
    { label: 'Sin contactar', value: snapshot.uncontacted, cls: (snapshot.uncontacted || 0) > 0 ? 'is-warn' : '' },
    {
      label: 'Estancados ' + th.staleDays + '+ d',
      value: snapshot.stale7,
      cls: (snapshot.stale7 || 0) > 0 ? 'is-warn' : ''
    },
    {
      label: 'Viejos ' + th.oldDays + '+ d',
      value: snapshot.old15,
      cls: (snapshot.old15 || 0) > 0 ? 'is-bad' : ''
    }
  ];
  const tilesHtml =
    '<div class="analytics-kpi-grid">' +
    tiles
      .map(
        (t) =>
          '<article class="shared-kpi-card' +
          (t.cls ? ' ' + t.cls : '') +
          '"><p class="shared-kpi-label">' +
          esc(t.label) +
          '</p><p class="shared-kpi-value">' +
          fmtNum(t.value) +
          '</p></article>'
      )
      .join('') +
    '</div>';

  const states = Array.isArray(snapshot.byState) ? snapshot.byState : [];
  const rowsHtml = !states.length
    ? '<p class="analytics-empty">No hay leads abiertos.</p>'
    : '<table class="analytics-table"><thead><tr>' +
      '<th>Estado interno</th>' +
      '<th>Ciclo</th>' +
      '<th class="num">Abiertos</th>' +
      '<th class="num">%</th>' +
      '<th class="num">Antig. mediana</th>' +
      '<th class="num">Más antiguo</th>' +
      '<th class="num">' + th.staleDays + '+ d</th>' +
      '<th class="num">' + th.oldDays + '+ d</th>' +
      '<th class="num">Sin contactar</th>' +
      '</tr></thead><tbody>' +
      states
        .map((s) => {
          return (
            '<tr>' +
            '<td><strong>' +
            esc(s.internalStateName || s.internalStateId || '—') +
            '</strong></td>' +
            '<td>' + cicloBadge(s.ciclo) + '</td>' +
            '<td class="num">' + fmtNum(s.count) + '</td>' +
            '<td class="num">' + (s.share != null ? fmtNum(s.share) + '%' : '—') + '</td>' +
            '<td class="num">' + (s.medianAgeDays != null ? fmtNum(s.medianAgeDays) + ' d' : '—') + '</td>' +
            '<td class="num">' + (s.oldestAgeDays != null ? fmtNum(s.oldestAgeDays) + ' d' : '—') + '</td>' +
            '<td class="num' + ((s.stale7 || 0) > 0 ? ' is-warn' : '') + '">' + fmtNum(s.stale7) + '</td>' +
            '<td class="num' + ((s.old15 || 0) > 0 ? ' is-bad' : '') + '">' + fmtNum(s.old15) + '</td>' +
            '<td class="num">' + fmtNum(s.uncontacted) + '</td>' +
            '</tr>'
          );
        })
        .join('') +
      '</tbody></table>';

  const gen = formatHumanDate(snapshot.generatedAt);
  const foot =
    '<p class="analytics-section-note" style="margin-top:8px">Foto en vivo del stock abierto · independiente del filtro de fechas' +
    (gen ? ' · ' + esc(gen) : '') +
    (Number(snapshot.noCreatedAt) > 0
      ? ' · ' + fmtNum(snapshot.noCreatedAt) + ' sin fecha de creación'
      : '') +
    '.</p>';

  return tilesHtml + rowsHtml + foot;
}

export function buildCohortHtml(cohort) {
  if (!cohort) {
    return '<p class="analytics-empty">Sin datos.</p>';
  }
  const steps = [
    {
      label: 'Ingresaron',
      value: cohort.totalEntered,
      hint: 'Creados en el período',
      cls: ''
    },
    {
      label: 'Siguen abiertos',
      value: cohort.stillOpen,
      hint:
        'Contacto ' +
        fmtNum(cohort.openContacto || 0) +
        ' · En proceso ' +
        fmtNum(cohort.openEnProceso || 0),
      cls: ''
    },
    {
      label: 'Contactados',
      value: cohort.contacted,
      hint: cohort.contacted === 0 ? 'Puede ser 0 por cobertura de seguimiento o mapeo de estados' : '',
      cls: ''
    },
    {
      label: 'Cliente / ganado',
      value: cohort.success,
      hint: '',
      cls: 'is-final is-success'
    },
    {
      label: 'Perdidos / no aplica / otros',
      value:
        (Number(cohort.lost) || 0) +
        (Number(cohort.notApplicable) || 0) +
        (Number(cohort.otherFinal) || 0),
      hint:
        'Perdido ' +
        fmtNum(cohort.lost) +
        ' · N/A ' +
        fmtNum(cohort.notApplicable) +
        ' · Otros ' +
        fmtNum(cohort.otherFinal),
      cls: 'is-final is-lost'
    }
  ];

  return (
    '<div class="analytics-story">' +
    steps
      .map((s) => {
        const tip = s.hint
          ? '<span class="analytics-story-hint" title="' + esc(s.hint) + '">' + esc(s.hint) + '</span>'
          : '';
        return (
          '<div class="analytics-story-step ' +
          esc(s.cls) +
          '">' +
          '<span class="analytics-story-marker" aria-hidden="true"></span>' +
          '<div><div class="analytics-story-label">' +
          esc(s.label) +
          '</div>' +
          tip +
          '</div>' +
          '<div class="analytics-story-value">' +
          fmtNum(s.value) +
          '</div></div>'
        );
      })
      .join('') +
    '</div>'
  );
}

export function buildFinalsHtml(finals, cohort) {
  if (!finals) {
    return '<p class="analytics-empty">Sin datos.</p>';
  }
  const entered = cohort && cohort.totalEntered != null ? Number(cohort.totalEntered) : 0;
  const items = [
    { label: 'Cliente / ganado', value: finals.success, cls: '' },
    { label: 'No aplica', value: finals.notApplicable, cls: 'is-warn' },
    { label: 'Perdido', value: finals.lost, cls: 'is-warn' },
    { label: 'Otros finales', value: finals.otherFinal, cls: '' }
  ];
  const max = Math.max.apply(
    null,
    items.map((i) => Number(i.value) || 0).concat([1])
  );
  return (
    '<div class="analytics-bars">' +
    items
      .map((r) => {
        const c = Number(r.value) || 0;
        const barPct = Math.round((c / max) * 100);
        const share = entered ? ' · ' + fmtPct(c, entered) + ' cohorte' : '';
        return (
          '<div class="analytics-bar-row"><div>' +
          esc(r.label) +
          '<span class="analytics-sub">' +
          fmtNum(c) +
          esc(share) +
          '</span></div><div class="analytics-bar-track"><div class="analytics-bar-fill' +
          (r.label === 'Perdido' ? ' is-warn' : r.label.indexOf('Cliente') === 0 ? '' : ' is-accent') +
          '" style="width:' +
          barPct +
          '%"></div></div><div class="analytics-num">' +
          fmtNum(c) +
          '</div></div>'
        );
      })
      .join('') +
    '</div>'
  );
}

export function buildSellersHtml(sellers, sortMode) {
  const mode = sortMode || 'criticity';
  const list = sortSellers(Array.isArray(sellers) ? sellers : [], mode);
  if (!list.length) {
    return '<p class="analytics-empty">Sin vendedores en leads abiertos.</p>';
  }
  return (
    '<table class="analytics-table"><thead><tr>' +
    '<th>Vendedor</th>' +
    '<th class="num" title="Leads abiertos asignados">Abiertos</th>' +
    '<th class="num">Sin contactar</th>' +
    '<th class="num">Fuera SLA</th>' +
    '<th class="num" title="Porcentaje visual: fuera SLA / abiertos">% fuera SLA</th>' +
    '</tr></thead><tbody>' +
    list
      .map((s) => {
        const lab = sellerLabel(s.sellerId, s.displayName);
        const open = Number(s.openLeads) || 0;
        const sla = Number(s.slaBreached) || 0;
        const p = pct(sla, open);
        const barW = p == null ? 0 : Math.min(100, Math.round(p));
        return (
          '<tr><td><strong>' +
          esc(lab.title) +
          '</strong>' +
          (lab.sub ? '<span class="analytics-sub">' + esc(lab.sub) + '</span>' : '') +
          '</td><td class="num">' +
          fmtNum(open) +
          '</td><td class="num">' +
          fmtNum(s.uncontacted) +
          '</td><td class="num">' +
          fmtNum(sla) +
          '</td><td class="num"><span class="analytics-crit-bar" aria-hidden="true"><span style="width:' +
          barW +
          '%"></span></span>' +
          (p == null ? '—' : String(p).replace('.', ',') + '%') +
          '</td></tr>'
        );
      })
      .join('') +
    '</tbody></table>'
  );
}

/**
 * @param {object} attention
 * @param {{ expanded?: boolean, previewSize?: number }} [opts]
 */
export function buildAttentionHtml(attention, opts) {
  const options = opts || {};
  const expanded = !!options.expanded;
  const preview =
    options.previewSize != null
      ? Number(options.previewSize)
      : ANALYTICS_CONFIG.attentionPreviewSize || 15;
  const total = attention && attention.total != null ? attention.total : 0;
  const items = (attention && attention.items) || [];
  if (!items.length) {
    return '<p class="analytics-empty">Ningún lead requiere atención según reglas actuales.</p>';
  }
  const show = expanded ? items : items.slice(0, preview);
  const canExpand = items.length > preview || total > preview;

  return (
    '<div class="analytics-attention-head"' +
    (canExpand ? ' data-expand-attention="1"' : '') +
    '>' +
    '<p class="analytics-section-note" style="margin:0">Mostrando ' +
    show.length +
    ' de ' +
    fmtNum(total) +
    ' críticos</p>' +
    (canExpand
      ? '<button type="button" class="analytics-btn analytics-btn-ghost" data-attention-toggle="1">' +
        (expanded ? 'Ver menos' : 'Ver todos los leads críticos') +
        '</button>'
      : '') +
    '</div>' +
    '<div class="analytics-panel-scroll"><table class="analytics-table"><thead><tr>' +
    '<th>Lead</th><th>Etapa</th><th>Vendedor</th><th class="num">Antigüedad</th><th>1er contacto</th><th>Motivo</th><th>Acción</th>' +
    '</tr></thead><tbody>' +
    show
      .map((a) => {
        const chips = (a.reasons || [])
          .filter((r) => r !== 'sla_pendiente')
          .map((r) => {
            const bad = r === 'fuera_sla_primer_contacto' || r === 'sin_contactar';
            return (
              '<span class="analytics-badge ' +
              (bad ? 'is-bad' : 'is-warn') +
              '">' +
              esc(reasonLabel(r)) +
              '</span>'
            );
          })
          .join('');
        const lab = sellerLabel(a.sellerId, null);
        const url = kommoLeadUrl(a.leadId);
        const contact =
          a.firstContactAt != null
            ? formatShortDate(a.firstContactAt) || 'Sí'
            : a.uncontacted
              ? 'Sin contacto'
              : '—';
        return (
          '<tr><td><strong>' +
          esc(a.name || 'Lead') +
          '</strong><span class="analytics-sub">' +
          esc(a.leadId || '') +
          '</span></td><td>' +
          esc(a.stageName || '—') +
          '</td><td>' +
          esc(lab.title) +
          (lab.sub ? '<span class="analytics-sub">' + esc(lab.sub) + '</span>' : '') +
          '</td><td class="num">' +
          (a.ageDaysSinceCreation != null ? fmtNum(a.ageDaysSinceCreation) + ' d' : '—') +
          '</td><td>' +
          esc(contact) +
          '</td><td><div class="analytics-badge-row">' +
          chips +
          '</div></td><td>' +
          (url
            ? '<a class="analytics-link-ext" href="' +
              esc(url) +
              '" target="_blank" rel="noopener noreferrer">Abrir en Kommo</a>'
            : '<span class="analytics-sub">—</span>') +
          '</td></tr>'
        );
      })
      .join('') +
    '</tbody></table></div>'
  );
}

export function buildQualityHtml(dq, stageDwell) {
  if (!dq) {
    return '<p class="analytics-empty">Sin datos de calidad.</p>';
  }
  const cov = dq.stageHistoryCoverage || {};
  const dwellTxt =
    stageDwell && stageDwell.available
      ? 'Disponible'
      : 'Disponible cuando se acumulen transiciones desde la instalación.';
  const items = [
    ['Leads en planilla', fmtNum(dq.leadsTotal)],
    ['Leads abiertos', fmtNum(dq.openLeads)],
    [
      'Antigüedad mediana (abiertos)',
      dq.openAgeDays && dq.openAgeDays.median != null ? fmtNum(dq.openAgeDays.median) + ' d' : 'Sin datos suficientes'
    ],
    [
      'Creado más reciente (abiertos)',
      dq.openCreatedAtRange && dq.openCreatedAtRange.newest
        ? formatHumanDate(dq.openCreatedAtRange.newest) || dash(dq.openCreatedAtRange.newest)
        : '—'
    ],
    [
      'Cobertura Seguimiento',
      dq.seguimientoCoverage != null
        ? String(dq.seguimientoCoverage).replace('.', ',') +
          '% · Sincroniza notas de Kommo para mejorar SLA'
        : 'Sin dato'
    ],
    ['Sin fecha de creación', fmtNum(dq.missingCreatedAt)],
    ['Abiertos sin vendedor', fmtNum(dq.missingSeller)],
    [
      'Historial exacto desde',
      cov.exactFrom ? formatHumanDate(cov.exactFrom) || dash(cov.exactFrom) : 'Aún no inicializado'
    ],
    ['Snapshot inicial', fmtNum(cov.initialSnapshotCount)],
    ['Tiempo en etapa', dwellTxt]
  ];
  return (
    '<ul class="analytics-quality-list">' +
    items
      .map(([k, v]) => '<li><span>' + esc(k) + '</span><strong>' + esc(v) + '</strong></li>')
      .join('') +
    '</ul>'
  );
}

export function buildWarningsHtml(list) {
  const warnings = (Array.isArray(list) ? list : [])
    .filter(Boolean)
    .filter(
      (w) =>
        !/Sales_Stage_Map|Sales_Internal_States|Sin mapeo Sales_Stage/i.test(
          String(w)
        )
    );
  if (!warnings.length) {
    return '';
  }
  const grouped = { critical: [], attention: [], info: [] };
  warnings.forEach((w) => {
    grouped[warningLevel(w)].push(w);
  });
  return ['critical', 'attention', 'info']
    .filter((lvl) => grouped[lvl].length)
    .map((lvl) => {
      const title =
        lvl === 'critical' ? 'Crítico' : lvl === 'attention' ? 'Atención' : 'Información';
      return (
        '<div class="analytics-alert is-' +
        lvl +
        '" role="status">' +
        '<div><strong>' +
        title +
        '</strong><ul class="analytics-alert-list">' +
        grouped[lvl].map((w) => '<li>' + esc(w) + '</li>').join('') +
        '</ul></div></div>'
      );
    })
    .join('');
}

/**
 * Reporte: ¿los leads del formulario web llegan a Kommo?
 * @param {object|null} health
 * @returns {string}
 */
export function buildWebKommoHealthHtml(health) {
  if (!health) return '';
  const status = String(health.status || 'unavailable');
  const lvl =
    status === 'critical' ? 'critical' : status === 'attention' ? 'attention' : status === 'ok' ? 'info' : 'attention';
  const title =
    status === 'ok'
      ? 'Web → Kommo: OK'
      : status === 'critical'
        ? 'Web → Kommo: se están perdiendo leads'
        : status === 'attention'
          ? 'Web → Kommo: revisar sync'
          : 'Web → Kommo: sin dato';

  const total = health.total != null ? fmtNum(health.total) : '—';
  const con = health.conKommo != null ? fmtNum(health.conKommo) : '—';
  const sin = health.sinKommo != null ? fmtNum(health.sinKommo) : '—';
  const rate =
    health.arrivalRate != null ? String(health.arrivalRate).replace('.', ',') + '%' : '—';
  const err = health.conError != null ? fmtNum(health.conError) : '0';
  const waFail = health.waNoEntregado != null ? fmtNum(health.waNoEntregado) : '0';

  let top = '';
  const fugas = Array.isArray(health.topFugas) ? health.topFugas : [];
  if (fugas.length) {
    top =
      '<div class="analytics-table-wrap" style="margin-top:10px"><table class="analytics-table"><thead><tr>' +
      '<th>Campaña web</th><th class="num">Sin Kommo</th><th class="num">Error</th><th class="num">Total</th>' +
      '</tr></thead><tbody>' +
      fugas
        .map(
          (r) =>
            '<tr><td>' +
            esc(r.campana) +
            '</td><td class="num' +
            (r.sinKommo ? ' is-bad' : '') +
            '">' +
            fmtNum(r.sinKommo) +
            '</td><td class="num">' +
            fmtNum(r.conError) +
            '</td><td class="num">' +
            fmtNum(r.total) +
            '</td></tr>'
        )
        .join('') +
      '</tbody></table></div>';
  }

  const href = health.href || '/fugas-meta';

  return (
    '<div class="analytics-alert is-' +
    lvl +
    '" role="status">' +
    '<div><strong>' +
    esc(title) +
    '</strong>' +
    '<p style="margin:6px 0 0">' +
    esc(health.message || '') +
    '</p>' +
    '<p class="analytics-section-note" style="margin:8px 0 0">' +
    'Registrados web <strong>' +
    total +
    '</strong> · Con Kommo <strong>' +
    con +
    '</strong> · Sin Kommo <strong>' +
    sin +
    '</strong> · Llegada <strong>' +
    rate +
    '</strong> · Errores sync <strong>' +
    err +
    '</strong> · WA no entregado <strong>' +
    waFail +
    '</strong></p>' +
    top +
    '<p style="margin:10px 0 0"><a class="analytics-btn analytics-btn-ghost" href="' +
    esc(href) +
    '">Abrir Fugas Meta</a></p>' +
    '</div></div>'
  );
}
